// src/components/modals/CreateCustomerGroupModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, Users, Plus, CheckSquare, Square, Filter, Search } from 'lucide-react'
import CustomerGroupService from '../../services/customerGroupService'
import CustomerService from '../../services/customerService'
import { CustomerGroupModel } from '../../utils/customerGroupModel'

const CreateCustomerGroupModal = ({ isOpen, onClose, onGroupCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customers: [],
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [availableCustomers, setAvailableCustomers] = useState([])
  const [customersLoading, setCustomersLoading] = useState(false)
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('name')
  const [filteredCustomers, setFilteredCustomers] = useState([])

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        description: '',
        customers: [],
        isActive: true
      })
      setErrors({})
      setCustomerSearchTerm('')
      setSortBy('name')
      fetchAvailableCustomers()
    }
  }, [isOpen])

  // Filter customers based on search term
  useEffect(() => {
    if (!Array.isArray(availableCustomers)) {
      setFilteredCustomers([])
      return
    }

    let filtered = [...availableCustomers]

    // Apply search filter
    if (customerSearchTerm.trim()) {
      const searchLower = customerSearchTerm.toLowerCase()
      filtered = filtered.filter(customer => 
        customer.name?.toLowerCase().includes(searchLower) ||
        customer.mobileNumber?.includes(customerSearchTerm)
      )
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '')
        case 'joinDate':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        case 'loyaltyPoints':
          return (b.pointsBalance || b.loyaltyPoints || 0) - (a.pointsBalance || a.loyaltyPoints || 0)
        default:
          return (a.name || '').localeCompare(b.name || '')
      }
    })

    setFilteredCustomers(filtered)
  }, [availableCustomers, customerSearchTerm, sortBy])

  // Fetch available customers
  const fetchAvailableCustomers = async () => {
    try {
      setCustomersLoading(true)
      
      // Fetch all active customers without any 1000 customer limit cap
      let allCustomers = []
      let page = 1
      let hasMore = true
      
      while (hasMore) {
        const response = await CustomerService.getAllCustomers({ 
          page: page, 
          limit: 500,
          isActive: true 
        })
        
        if (response?.success) {
          const customers = response.data?.customers || []
          allCustomers = [...allCustomers, ...customers]
          
          // Check if there are more pages
          const totalPages = response.data?.totalPages || 1
          hasMore = page < totalPages && customers.length > 0
          page++
        } else {
          hasMore = false
        }
      }
      
      setAvailableCustomers(allCustomers)
    } catch (error) {
      console.error('❌ Error fetching customers:', error)
    } finally {
      setCustomersLoading(false)
    }
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle customer selection
  const handleCustomerToggle = (customer, event) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation()
    }
    
    setFormData(prev => {
      const isSelected = prev.customers.some(c => c._id === customer._id)
      
      if (isSelected) {
        // Remove customer
        return {
          ...prev,
          customers: prev.customers.filter(c => c._id !== customer._id)
        }
      } else {
        // Add customer
        return {
          ...prev,
          customers: [...prev.customers, customer]
        }
      }
    })
  }

  // Check if customer is selected
  const isCustomerSelected = (customer) => {
    return formData.customers.some(c => c._id === customer._id)
  }

  // Select all filtered customers
  const selectAllCustomers = () => {
    setFormData(prev => {
      // Get all customer IDs that are already selected
      const selectedIds = new Set(prev.customers.map(c => c._id))
      
      // Add all filtered customers that aren't already selected
      const newCustomers = [...prev.customers]
      filteredCustomers.forEach(customer => {
        if (!selectedIds.has(customer._id)) {
          newCustomers.push(customer)
        }
      })
      
      return {
        ...prev,
        customers: newCustomers
      }
    })
  }

  // Clear all selected customers
  const clearAllCustomers = () => {
    setFormData(prev => ({
      ...prev,
      customers: []
    }))
  }

  // Check if all filtered customers are selected
  const areAllFilteredSelected = () => {
    if (filteredCustomers.length === 0) return false
    return filteredCustomers.every(customer => isCustomerSelected(customer))
  }

  // Validate form
  const validateForm = () => {
    const validation = CustomerGroupModel.validate(formData)
    setErrors(validation.errors)
    return validation.isValid
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      
      const groupData = CustomerGroupService.formatCustomerGroupForAPI(formData)
      const response = await CustomerGroupService.createCustomerGroup(groupData)
      
      if (response?.success) {
        console.log('✅ Customer group created successfully:', response.data)
        
        if (onGroupCreated) {
          onGroupCreated(response.data)
        }
        
        onClose()
      } else {
        setErrors({ general: response?.message || 'Failed to create customer group' })
      }
    } catch (error) {
      console.error('❌ Error creating customer group:', error)
      
      if (error.response?.status === 409) {
        setErrors({ name: 'A customer group with this name already exists' })
      } else if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || error.message
        if (errorMessage.toLowerCase().includes('name')) {
          setErrors({ name: errorMessage })
        } else {
          setErrors({ general: errorMessage })
        }
      } else {
        setErrors({ general: error.message || 'Failed to create customer group' })
      }
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 ">
          <div className="flex items-center space-x-2">
            {/* <div className="p-2 bg-orange-100 rounded-lg">
              <Users className="h-5 w-5 text-orange-600" />
            </div> */}
            <h2 className="text-xl font-semibold text-gray-900">Create Group</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {/* General Error */}
              {errors.general && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{errors.general}</p>
                </div>
              )}

              {/* Group Information */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Group Information</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Group Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Group Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="All Customers"
                      className={errors.name ? 'border-red-300 focus:border-red-500' : ''}
                      disabled={loading}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  </div>

                  {/* Status Switch */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                        Status
                      </label>
                      {/* <p className="text-xs text-gray-500">
                        {formData.isActive ? 'Group is active and visible' : 'Group is inactive and hidden'}
                      </p> */}
                    </div>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                        disabled={loading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                          formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.isActive ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      {/* <span className="ml-3 text-sm text-gray-700">
                        {formData.isActive ? 'Active' : 'Inactive'}
                      </span> */}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe about the group..."
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none ${
                      errors.description ? 'border-red-300 focus:border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading}
                  />
                  {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                </div>
              </div>

              {/* Customer Selection */}
              <div>
                {/* Filter Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Filter className="h-5 w-5 text-gray-600" />
                    <h3 className="text-lg font-medium text-gray-900">Filter Customers</h3>
                  </div>
                  {(customerSearchTerm || sortBy !== 'name') && (
                    <Button
                      type="button"
                      onClick={() => {
                        setCustomerSearchTerm('')
                        setSortBy('name')
                      }}
                      variant="outline"
                      size="sm"
                      className="text-orange-500 border-orange-500 hover:bg-orange-50"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
                
                {/* Search and Sort Controls */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* Search */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
                    <Input
                      type="text"
                      placeholder="Search by Name, Mobile number.."
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      className="pl-10"
                      disabled={customersLoading}
                    />
                  </div>

                  {/* Sort By */}
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white appearance-none pr-10"
                      disabled={customersLoading}
                    >
                      <option value="name">Name (A-Z)</option>
                      <option value="joinDate">Join Date</option>
                      <option value="loyaltyPoints">Loyalty Points</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Results Count */}
                  <div className="flex items-center justify-end">
                    <span className="text-sm text-gray-600">
                      Showing {filteredCustomers.length} of {availableCustomers.length} Customers
                    </span>
                  </div>
                </div>

                {/* Selected Customers Count */}
                {formData.customers.length > 0 && (
                  <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-800">
                      {formData.customers.length} customer{formData.customers.length !== 1 ? 's' : ''} selected
                    </p>
                  </div>
                )}

                {/* Customer List */}
                <div className="border rounded-lg">
                  <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">Customer List</h4>
                    <div className="flex items-center space-x-2">
                      {filteredCustomers.length > 0 && (
                        <>
                          <Button
                            type="button"
                            onClick={selectAllCustomers}
                            variant="outline"
                            size="sm"
                            disabled={areAllFilteredSelected()}
                            className="text-xs"
                          >
                            <CheckSquare className="h-3 w-3 mr-1" />
                            Select All
                          </Button>
                          <Button
                            type="button"
                            onClick={clearAllCustomers}
                            variant="outline"
                            size="sm"
                            disabled={formData.customers.length === 0}
                            className="text-xs"
                          >
                            <Square className="h-3 w-3 mr-1" />
                            Clear All
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {customersLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
                        <span className="ml-2 text-gray-600">Loading customers...</span>
                      </div>
                    ) : filteredCustomers.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600">No customers found</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {/* Table Header */}
                        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700">
                          <div className="col-span-1"></div>
                          <div className="col-span-2">Join Date</div>
                          <div className="col-span-3">Name</div>
                          <div className="col-span-2">Mobile number</div>
                          <div className="col-span-2">DOB</div>
                          <div className="col-span-2">Loyalty Points</div>
                        </div>
                        
                        {/* Table Body */}
                        {filteredCustomers.map((customer) => (
                          <div 
                            key={customer._id} 
                            className={`grid grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer ${
                              isCustomerSelected(customer) ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                            }`}
                            onClick={() => handleCustomerToggle(customer)}
                          >
                            <div className="col-span-1 flex items-center">
                              <input
                                type="checkbox"
                                checked={isCustomerSelected(customer)}
                                onChange={(e) => handleCustomerToggle(customer, e)}
                                onClick={(e) => e.stopPropagation()}
                                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                              />
                            </div>
                            <div className="col-span-2 text-sm text-gray-900">
                              {new Date(customer.createdAt).toLocaleDateString('en-GB')}
                            </div>
                            <div className="col-span-3 text-sm font-medium text-gray-900">
                              {customer.name}
                            </div>
                            <div className="col-span-2 text-sm text-gray-900">
                              {customer.mobileNumber}
                            </div>
                            <div className="col-span-2 text-sm text-gray-900">
                              {customer.dateOfBirth ? new Date(customer.dateOfBirth).toLocaleDateString('en-GB') : 'N/A'}
                            </div>
                            <div className="col-span-2 text-sm text-gray-900">
                              {((customer.pointsBalance || customer.loyaltyPoints || 0)).toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-orange-600 hover:bg-orange-700 text-white"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Save & Create
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateCustomerGroupModal